import { endPointRefreshToken } from "./endpoints";
import { getVisitorId } from "utils/analytics";
import { PlanRequiredError } from "./errors";

interface ApiResponse<T = unknown> {
  data?: T;
  message?: string;
  retry_request?: boolean;
  authenticated?: boolean;
  user_id?: string;
  userData?: unknown;
  user?: unknown;
  status?: string;
}

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

interface RetryState {
  tried: boolean;
}

class ApiClient {
  private isRefreshing = false;
  private isLoggingOut = false;
  private refreshPromise: Promise<Response> | null = null;

  constructor() {
    if (typeof window !== "undefined") {
      window.addEventListener("auth:logout", () => {
        this.isLoggingOut = true;
      });
    }
  }

  private notifyAuthLogout(): void {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("auth:logout"));
    }
  }

  private reportError(error: unknown, context: string): void {
    if (typeof window === "undefined") {
      return;
    }

    const errorReporting = (window as Window & {
      errorReporting?: { captureException: (exception: unknown, metadata?: unknown) => void };
    }).errorReporting;

    errorReporting?.captureException(error, { context });
  }

  private async manualRefresh(): Promise<Response> {
    if (this.isLoggingOut) {
      return new Response(null, { status: 401, statusText: "Logout in progress" });
    }

    if (!this.refreshPromise) {
      this.isRefreshing = true;
      this.refreshPromise = fetch(endPointRefreshToken, {
        method: "POST",
        credentials: "include",
      }).finally(() => {
        this.refreshPromise = null;
        this.isRefreshing = false;
      });
    }

    return this.refreshPromise;
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    if (response.status === 204) {
      return null as T;
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      return await response.json() as T;
    }

    return await response.text() as T;
  }

  private buildRequestOptions(options: FetchOptions): FetchOptions {
    const headers = new Headers(options.headers);
    const bodyIsFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
    const visitorId = getVisitorId();

    if (!headers.has("Content-Type") && !bodyIsFormData) {
      headers.set("Content-Type", "application/json");
    }

    if (visitorId && !headers.has("x-visitor-id")) {
      headers.set("x-visitor-id", visitorId);
    }

    return {
      ...options,
      credentials: "include",
      headers: Object.fromEntries(headers.entries()),
    };
  }

  private shouldRetryRequest(data: unknown): boolean {
    if (!data || typeof data !== "object") {
      return false;
    }

    return (data as ApiResponse).retry_request === true;
  }

  async request<T = unknown>(
    url: string,
    options: FetchOptions = {},
    retryState: RetryState = { tried: false }
  ): Promise<T> {
    const requestOptions = this.buildRequestOptions(options);

    try {
      const response = await fetch(url, requestOptions);

      if (response.ok) {
        const data = await this.parseResponse<T>(response);

        // Backend contract: when middleware refreshed cookies, retry once.
        if (this.shouldRetryRequest(data) && !retryState.tried) {
          return this.request<T>(url, options, { tried: true });
        }

        return data;
      }

      // Backend contract: one manual fallback to /api/refresh-token on 401.
      if (response.status === 401 && !retryState.tried && !this.isLoggingOut) {
        const refreshResponse = await this.manualRefresh();

        if (refreshResponse.ok) {
          return this.request<T>(url, options, { tried: true });
        }

        if (refreshResponse.status === 401) {
          this.notifyAuthLogout();
          throw new Error("Session expired");
        }
      }

      if (response.status === 403) {
        try {
          const errBody = (await response.clone().json()) as {
            error?: unknown;
            required_plan?: unknown;
            detail?: unknown;
          };
          if (
            errBody &&
            errBody.error === "plan_required" &&
            typeof errBody.required_plan === "string" &&
            typeof errBody.detail === "string"
          ) {
            throw new PlanRequiredError({
              error: "plan_required",
              required_plan: errBody.required_plan,
              detail: errBody.detail,
            });
          }
        } catch (e) {
          if (e instanceof PlanRequiredError) throw e;
          // fall through to the generic 403 throw below
        }
      }

      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    } catch (error) {
      this.reportError(error, `apiClient.request(${url})`);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error("Network error");
    }
  }

  get<T = unknown>(url: string, options?: FetchOptions): Promise<T> {
    return this.request<T>(url, { ...options, method: "GET" });
  }

  post<T = unknown>(url: string, data?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T = unknown>(url: string, data?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T = unknown>(url: string, data?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T = unknown>(url: string, options?: FetchOptions): Promise<T> {
    return this.request<T>(url, { ...options, method: "DELETE" });
  }

  resetLogoutState(): void {
    this.isLoggingOut = false;
  }

  get isTokenRefreshing(): boolean {
    return this.isRefreshing;
  }
}

const apiClient = new ApiClient();

export default apiClient;
export type { ApiResponse, FetchOptions };